import { useNavigate, useParams } from 'react-router-dom';
import { partnerWorkerAlerts as alerts, useAlert } from 'shared/components/alerts';
import { FormProvider, useForm } from 'react-hook-form';
import { Header, Page } from 'redesign';
import { usePartnerRelativePath } from 'partner/shared/hooks/use-partner-relative-path';
import { PARTNER_PATHS } from 'partner/paths';
import { RenderUnsettledUI } from 'shared/components';
import { invariant } from 'shared/utils/utils';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import { PartnerWorkerFormValues } from './partner-worker-form.types';
import { useUpdatePartnerWorker } from './hooks/use-update-partner-worker';
import { PartnerWorkerFormDataProvider } from './partner-worker-form';
import { useGetPartnerWorker } from '../hooks/use-get-partner-worker';

const PartnerWorkerUpdate = () => {
  const { id } = useParams<'id'>();
  invariant(id, 'Partner worker ID was supposed to be passed via route param');

  const { status, error, refetch, data } = useGetPartnerWorker(id);
  if (status !== 'success') {
    return <RenderUnsettledUI data={{ error, refetch, status }} />;
  }
  return <PartnerWorkerUpdateForm id={id} data={data} />;
};

const PartnerWorkerUpdateForm = ({ id, data }: { id: string; data: PartnerWorkerFormValues }) => {
  const { alertSuccess, alertError } = useAlert();
  const navigate = useNavigate();
  const generatePath = usePartnerRelativePath();
  const mutation = useUpdatePartnerWorker();
  const formMethods = useForm<PartnerWorkerFormValues>({ defaultValues: data });

  const handleSubmit = () => {
    formMethods.handleSubmit((data) =>
      mutation.mutate(
        { partnerWorkerId: id, payload: data },
        {
          onSuccess: () => {
            navigate(generatePath(PARTNER_PATHS.Worker));
            alertSuccess(alerts.success.updated);
          },
          onError: (e) => alertError(e),
        }
      )
    )();
  };

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText="Kandidaat bijwerken" />}
        toolbox={
          <ToolboxForm
            onSubmit={handleSubmit}
            mutationStatus={mutation.status}
            mode="update"
            onCancel={() => navigate(generatePath(PARTNER_PATHS.Worker))}
          />
        }
      >
        <PartnerWorkerFormDataProvider />
      </Page>
    </FormProvider>
  );
};

export { PartnerWorkerUpdate };
